import { CircularProgress } from '@mui/material';
import type { ISuggestion } from '@sharedInterfaces/IEmployeeSuggestions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CertificatesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CertificatesInput/CertificatesInput';
import EmployeeSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/EmployeeSelect/EmployeeSelect';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import RolesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/RolesInput/RolesInput';
import SkillsInput from '@src/App/Components/General/FormularElements/formsControls/inputs/SkillsInput/SkillsInput';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ErrorBox from '../../Error/ErrorBox/ErrorBox';
import Row from '../../General/FormularElements/Row/Row';
import RowElement from '../../General/FormularElements/RowElement/RowElement';

export interface SuggestionFormProps {
    suggestion?: ISuggestion;
    employeeId?: number;
    errorText?: string;
    saving?: boolean;
    showEmployeeSelect?: boolean;
    requireJustification?: boolean;
    enabledEntities?: {
        skills?: boolean;
        certificates?: boolean;
        roles?: boolean;
    };
    onEmployeeChange?: (employeeId: number) => void;
    onSkillsChange?: (skills: { title: string; id?: number; level: number }[]) => void;
    onCertificatesChange?: (certificates: { title: string; id?: number }[]) => void;
    onRolesChange?: (roles: { title: string; id: number }[]) => void;
    onJustificationChange?: (justification: string) => void;
}

export function SuggestionForm({
    suggestion,
    employeeId: initialEmployeeId,
    errorText,
    saving = false,
    showEmployeeSelect = true,
    requireJustification = false,
    enabledEntities = { skills: true, certificates: true, roles: true },
    onEmployeeChange,
    onSkillsChange,
    onCertificatesChange,
    onRolesChange,
    onJustificationChange,
}: SuggestionFormProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).suggestions;
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates || []);
    const allRoles = useSelector((state: AppState) => state.company.allRoles || []);

    // Lokaler State für die Formularwerte
    const [employeeIdValue, setEmployeeIdValue] = useState<number>(
        suggestion ? suggestion.employeeId : initialEmployeeId !== undefined ? initialEmployeeId : -1,
    );
    const [skillsValue, setSkillsValue] = useState<{ title: string; id?: number; level: number }[]>(
        suggestion ? suggestion.elements.skills.map((s) => ({ ...s, level: s.level || 0 })) : [],
    );
    const [certificatesValue, setCertificatesValue] = useState<{ title: string; id?: number }[]>(
        suggestion ? suggestion.elements.certificates : [],
    );
    const [rolesValue, setRolesValue] = useState<{ title: string; id: number }[]>(
        suggestion ? suggestion.elements.roles : [],
    );
    const [justificationValue, setJustificationValue] = useState<string>(suggestion ? suggestion.justification : '');

    // Aktualisiere den lokalen State, wenn sich die Props ändern
    useEffect(() => {
        if (suggestion) {
            setEmployeeIdValue(suggestion.employeeId);
            setSkillsValue(suggestion.elements.skills.map((s) => ({ ...s, level: s.level || 0 })));
            setCertificatesValue(suggestion.elements.certificates);
            setRolesValue(suggestion.elements.roles);
            setJustificationValue(suggestion.justification);
        } else if (initialEmployeeId !== undefined) {
            setEmployeeIdValue(initialEmployeeId);
        }
    }, [suggestion, initialEmployeeId]);

    // Handler für Änderungen
    const handleEmployeeChange = (value: number) => {
        setEmployeeIdValue(value);
        if (onEmployeeChange) {
            onEmployeeChange(value);
        }
    };

    const handleSkillsChange = (newSkills: { title: string; id?: number; level: number }[]) => {
        setSkillsValue(newSkills);
        if (onSkillsChange) {
            onSkillsChange(newSkills);
        }
    };

    const handleCertificatesChange = (newCertificates: { title: string; id?: number }[]) => {
        setCertificatesValue(newCertificates);
        if (onCertificatesChange) {
            onCertificatesChange(newCertificates);
        }
    };

    const handleRolesChange = (newRoles: { title: string; id: number }[]) => {
        setRolesValue(newRoles);
        if (onRolesChange) {
            onRolesChange(newRoles);
        }
    };

    const handleJustificationChange = (value: string) => {
        setJustificationValue(value);
        if (onJustificationChange) {
            onJustificationChange(value);
        }
    };

    return (
        <div className="suggestion-form-content">
            {errorText && <ErrorBox>{errorText}</ErrorBox>}

            {showEmployeeSelect && (
                <Row>
                    <RowElement title={langStrings.employee}>
                        <EmployeeSelect
                            value={employeeIdValue}
                            onChange={(value) => value && handleEmployeeChange(value)}
                            disabled={!!suggestion || initialEmployeeId !== undefined}
                        />
                    </RowElement>
                </Row>
            )}

            {enabledEntities.skills && (
                <Row>
                    <RowElement title={langStrings.oftenUsed.skills}>
                        <SkillsInput
                            allSkills={allSkills}
                            skills={skillsValue}
                            onChange={handleSkillsChange}
                            size="medium"
                            allowNewSkills={true}
                        />
                    </RowElement>
                </Row>
            )}

            {enabledEntities.certificates && (
                <Row>
                    <RowElement title={langStrings.oftenUsed.certificates}>
                        <CertificatesInput
                            allCertificates={allCertificates}
                            selectedCertificates={certificatesValue}
                            onChange={handleCertificatesChange}
                            size="medium"
                        />
                    </RowElement>
                </Row>
            )}

            {enabledEntities.roles && (
                <Row>
                    <RowElement title={langStrings.oftenUsed.roles}>
                        <RolesInput
                            allRoles={allRoles}
                            selectedRoles={rolesValue}
                            onChange={handleRolesChange}
                            showMode={false}
                            size="medium"
                        />
                    </RowElement>
                </Row>
            )}

            <Row>
                <RowElement title={langStrings.justification}>
                    <FormatedTextInput
                        value={justificationValue}
                        onChange={handleJustificationChange}
                        placeholder={langStrings.justificationPlaceholder}
                        fullWidth
                        // required={requireJustification}
                    />
                </RowElement>
            </Row>

            {suggestion && suggestion.feedback && (
                <Row>
                    <RowElement title={langStrings.feedbackFromEmployee}>
                        <FormatedTextInput value={suggestion.feedback} readonly={true} fullWidth />
                    </RowElement>
                </Row>
            )}
        </div>
    );
}

export default SuggestionForm;
